import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface ChargeDetail {
  cn10YearlyTotal: string;
  rate: string;
  total: string;
}

interface IbrsCharge {
  sendingDo: string;
  receivingDo: string;
  normalWeight: ChargeDetail;
  normalItems: ChargeDetail;
  bulkyWeight: ChargeDetail;
  bulkyItems: ChargeDetail;
  total: string;
}

interface CN19ChargesTableProps {
  title: string;
  inboundCharge: IbrsCharge;
  outboundCharge: IbrsCharge;
  totalCharge: string;
  currency: string;
  creditor: string;
}

// Styled components
const StyledTableContainer = styled('div')(() => ({
  overflowX: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  marginBottom: '24px',
}));

const StyledTable = styled(Table)(() => ({
  minWidth: 500,
}));

const HeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'center',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  border: '1px solid #D1D9E0',
}));

const SubTableHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#2196F3',
  color: 'white',
  padding: theme.spacing(0.5, 1),
  fontSize: '12px',
  fontWeight: 700,
  textAlign: 'center',
  borderRadius: '4px 4px 0 0',
}));

const DataCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFFFFF',
  padding: theme.spacing(0.75, 1),
  border: '1px solid #E1E8ED',
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 500,
  },
  '&.label-cell': {
    textAlign: 'left',
    fontWeight: 600,
    backgroundColor: '#F8F9FA',
  },
}));

const SectionHeaderCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#E3F2FD',
  padding: theme.spacing(1, 1.5),
  fontWeight: 700,
  color: '#063142',
  border: '1px solid #D1D9E0',
}));

const TotalCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  textAlign: 'center',
  backgroundColor: '#FFF3E0',
  padding: theme.spacing(0.75, 1),
  border: '1px solid #D1D9E0',
  fontWeight: 700,
  '&.number-cell': {
    fontFamily: 'monospace',
    textAlign: 'right',
    fontWeight: 700,
  },
}));

interface ChargeTableProps {
  title: string;
  sendingDo: string;
  receivingDo: string;
  charge: IbrsCharge;
  currency: string;
  type?: 'inbound' | 'outbound';
}

function SingleChargeTable({ title, sendingDo, receivingDo, charge, currency, type }: ChargeTableProps) {
  // Display raw string value as received from backend
  const formatValue = (value: string | number): string => {
    if (!value && value !== 0) return '';
    if (value === '-') return '-';
    return String(value);
  };

  // Determine background color based on type
  const getBackgroundColor = () => {
    if (type === 'inbound') return 'rgb(230, 245, 230)';
    if (type === 'outbound') return 'rgb(255, 240, 240)';
    return '#FFFFFF';
  };

  return (
    <Box sx={{ mb: 3 }} data-testid={`cn19-charge-table-${title.toLowerCase()}`}>
      <SubTableHeader data-testid={`cn19-charge-header-${title.toLowerCase()}`}>
        {title} - Sending DO: {sendingDo}, Receiving DO: {receivingDo}
      </SubTableHeader>
      <StyledTableContainer data-testid={`cn19-charge-container-${title.toLowerCase()}`}>
        <StyledTable data-testid={`cn19-charge-table-${title.toLowerCase()}`}>
          <TableHead data-testid={`cn19-charge-head-${title.toLowerCase()}`}>
            <TableRow data-testid={`cn19-charge-header-row-${title.toLowerCase()}`}>
              <HeaderCell colSpan={2} style={{ minWidth: '200px' }} data-testid={`cn19-charge-header-cell-dos-${title.toLowerCase()}`}>
                Sending DO: {sendingDo} Receiving DO: {receivingDo}
              </HeaderCell>
              <HeaderCell rowSpan={2} style={{ minWidth: '120px' }} data-testid={`cn19-charge-header-cell-total-${title.toLowerCase()}`}>
                Yearly Total CN10
              </HeaderCell>
              <HeaderCell rowSpan={2} style={{ minWidth: '100px' }} data-testid={`cn19-charge-header-cell-rate-${title.toLowerCase()}`}>
                Rate ({currency})
              </HeaderCell>
              <HeaderCell rowSpan={2} style={{ minWidth: '120px' }} data-testid={`cn19-charge-header-cell-total-td-${title.toLowerCase()}`}>
                Total TD ({currency})
              </HeaderCell>
            </TableRow>
          </TableHead>

          <TableBody data-testid={`cn19-charge-body-${title.toLowerCase()}`}>
            {/* Normal IBRS Section */}
            <TableRow>
              <SectionHeaderCell rowSpan={2}>Normal IBRS</SectionHeaderCell>
              <DataCell className="label-cell" style={{ backgroundColor: getBackgroundColor() }}>Weight</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalWeight.cn10YearlyTotal)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalWeight.rate)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalWeight.total)}</DataCell>
            </TableRow>
            <TableRow>
              <DataCell className="label-cell" style={{ backgroundColor: getBackgroundColor() }}>Items</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalItems.cn10YearlyTotal)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalItems.rate)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.normalItems.total)}</DataCell>
            </TableRow>

            {/* Bulky IBRS Section */}
            <TableRow>
              <SectionHeaderCell rowSpan={2}>Bulky IBRS</SectionHeaderCell>
              <DataCell className="label-cell" style={{ backgroundColor: getBackgroundColor() }}>Weight</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyWeight.cn10YearlyTotal)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyWeight.rate)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyWeight.total)}</DataCell>
            </TableRow>
            <TableRow>
              <DataCell className="label-cell" style={{ backgroundColor: getBackgroundColor() }}>Items</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyItems.cn10YearlyTotal)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyItems.rate)}</DataCell>
              <DataCell className="number-cell" style={{ backgroundColor: getBackgroundColor() }}>{formatValue(charge.bulkyItems.total)}</DataCell>
            </TableRow>

            {/* Total Row */}
            <TableRow>
              <TotalCell colSpan={2} style={{ textAlign: 'center', fontWeight: 700 }}>
                Total ({sendingDo} to receive from {receivingDo})
              </TotalCell>
              <TotalCell colSpan={2}></TotalCell>
              <TotalCell className="number-cell" style={{ fontWeight: 700, backgroundColor: '#E8F5E8' }}>
                {formatValue(charge.total)}
              </TotalCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </Box>
  );
}

function CN19ChargesTable({ 
  title, 
  inboundCharge, 
  outboundCharge, 
  totalCharge, 
  currency,
  creditor 
}: CN19ChargesTableProps): React.ReactElement {
  // Display raw string value as received from backend
  const formatCurrency = (value: string | number): string => {
    if (!value && value !== 0) return '';
    if (value === '-') return '-';
    return String(value);
  };

  return (
    <Box sx={{ mb: 4 }} data-testid="cn19-charges-table-container">
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142', mb: 3 }} data-testid="cn19-charges-table-title">
        {title}
      </Typography>

      {/* Inbound Charges Table */}
      <SingleChargeTable
        title="Inbound"
        sendingDo={inboundCharge.sendingDo }
        receivingDo={inboundCharge.receivingDo }
        charge={inboundCharge}
        currency={currency}
        type="inbound"
        data-testid="cn19-charges-inbound-table"
      />

      {/* Outbound Charges Table */}
      <SingleChargeTable
        title="Outbound"
        sendingDo={outboundCharge.sendingDo }
        receivingDo={outboundCharge.receivingDo }
        charge={outboundCharge}
        currency={currency}
        type="outbound"
        data-testid="cn19-charges-outbound-table"
      />

      {/* Summary Information */}
      <Box sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: '#F8F9FA', 
        borderRadius: '8px',
        border: '1px solid #E1E8ED'
      }} data-testid="cn19-charges-summary">
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142', mb: 1 }} data-testid="cn19-charges-creditor">
          Creditor DO: <span style={{ fontWeight: 800 }}>{creditor}</span>
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn19-charges-total-amount">
          Total amount for {creditor} to be received ({currency}): {' '}
          <span style={{ 
            fontWeight: 800, 
            color: '#28A745',
            fontSize: '16px'
          }} data-testid="cn19-charges-total-value">
            {formatCurrency(totalCharge)}
          </span>
        </Typography>
      </Box>
    </Box>
  );
}

export default CN19ChargesTable; 